import React, { useRef } from 'react';
import { Button, Checkbox, Modal, TextInput } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useVirtualizer } from '@tanstack/react-virtual';
import { NumPadModal } from '../../../../components/NumPad/NumPad';
import { LimitItemRow } from './LimitItemRow';
import classes from './pos-limit-items-section.module.scss';
import type { PosLimitItemsSectionProps } from './types';
import { usePosLimitItemsController } from './usePosLimitItemsController';

const ROW_ESTIMATED_HEIGHT = 64;

// Architectural summary: POS "Limit Items" tab composition with UI-local state only.
// Function library: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-poslimititemssection
const PosLimitItemsSectionComponent: React.FC<PosLimitItemsSectionProps> = (props) => {
  const {
    search,
    statFilter,
    selectedProductIds,
    isSavingLimit,
    limitError,
    resetConfirm,
    filteredProducts,
    totalCount,
    outOfStockCount,
    limitedCount,
    hasClearAction,
    clearButtonLabel,
    allFilteredSelected,
    someFilteredSelected,
    hasSelectionActions,
    numPadOpen,
    numPadTitle,
    numPadSubject,
    numPadInitial,
    resetConfirmMessage,
    setSearch,
    setStatFilter,
    setResetConfirm,
    handleToggleSelectAllFiltered,
    handleOpenBulkNumPad,
    handleClearSelection,
    handleClearLimits,
    handleToggleSelected,
    handleOpenNumPad,
    handleClearLimit,
    handleCloseNumPad,
    handleConfirmNumPad,
    handleConfirmReset,
  } = usePosLimitItemsController(props);

  const { isManager, isLoadingPosProducts, posProducts } = props;
  const interactionsLocked = isSavingLimit;

  const listScrollRef = useRef<HTMLDivElement | null>(null);
  const rowVirtualizer = useVirtualizer({
    count: filteredProducts.length,
    getScrollElement: () => listScrollRef.current,
    estimateSize: () => ROW_ESTIMATED_HEIGHT,
    overscan: 8,
    getItemKey: (index) => filteredProducts[index].id,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const showList = !(isLoadingPosProducts && posProducts.length === 0) && filteredProducts.length > 0;

  return (
    <section className={classes.shell} aria-labelledby="pos-limit-items-heading" dir="rtl">
      <h2 id="pos-limit-items-heading" className={classes.srOnly}>
        הגבלת פריטים
      </h2>

      <div className={classes.statsRow}>
        <div
          className={`${classes.statCard} ${!isManager ? classes.statCardReadOnly : ''} ${isManager && statFilter === 'all' ? classes.statCardActive : ''}`}
          data-testid="limit-items-stat-all"
          role={isManager ? 'button' : undefined}
          tabIndex={isManager ? 0 : undefined}
          onClick={isManager ? () => setStatFilter('all') : undefined}
          onKeyDown={
            isManager
              ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setStatFilter('all');
                  }
                }
              : undefined
          }
          aria-pressed={isManager ? statFilter === 'all' : undefined}
          aria-label={isManager ? 'הצג את כל הפריטים' : undefined}
        >
          <span className={classes.statValue}>{totalCount}</span>
          <span className={classes.statLabel}>סה&quot;כ פריטים</span>
        </div>
        <div
          className={`${classes.statCard} ${!isManager ? classes.statCardReadOnly : ''} ${isManager && statFilter === 'limited' ? classes.statCardActive : ''}`}
          data-testid="limit-items-stat-limited"
          role={isManager ? 'button' : undefined}
          tabIndex={isManager ? 0 : undefined}
          onClick={isManager ? () => setStatFilter('limited') : undefined}
          onKeyDown={
            isManager
              ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setStatFilter('limited');
                  }
                }
              : undefined
          }
          aria-pressed={isManager ? statFilter === 'limited' : undefined}
          aria-label={isManager ? 'הצג מוגבלים' : undefined}
        >
          <span className={`${classes.statValue} ${classes.statValueInfo}`}>{limitedCount}</span>
          <span className={classes.statLabel}>מוגבלים</span>
        </div>
        <div
          className={`${classes.statCard} ${!isManager ? classes.statCardReadOnly : ''} ${isManager && statFilter === 'out' ? classes.statCardActive : ''}`}
          data-testid="limit-items-stat-out"
          role={isManager ? 'button' : undefined}
          tabIndex={isManager ? 0 : undefined}
          onClick={isManager ? () => setStatFilter('out') : undefined}
          onKeyDown={
            isManager
              ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setStatFilter('out');
                  }
                }
              : undefined
          }
          aria-pressed={isManager ? statFilter === 'out' : undefined}
          aria-label={isManager ? 'הצג אזל מהמלאי' : undefined}
        >
          <span className={`${classes.statValue} ${classes.statValueDanger}`}>{outOfStockCount}</span>
          <span className={classes.statLabel}>אזל מהמלאי</span>
        </div>
      </div>

      <div className={classes.searchRow}>
        <TextInput
          type="search"
          data-testid="limit-items-search"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder="חפש מוצרים..."
          aria-label="חפש מוצרים"
          leftSection={<FontAwesomeIcon icon={faMagnifyingGlass as IconProp} className={classes.searchIcon} />}
          classNames={{
            root: classes.searchInputRoot,
            input: classes.searchInput,
            section: classes.searchInputSection,
          }}
        />
      </div>

      <div className={classes.listCard}>
        <div className={classes.listHeader}>
          <div className={classes.productHeaderCluster}>
            {isManager && search.trim() !== '' && filteredProducts.length > 0 && (
              <span className={classes.headerCheckbox}>
                <Checkbox
                  data-testid="limit-items-select-all"
                  checked={allFilteredSelected}
                  indeterminate={someFilteredSelected}
                  onChange={() => handleToggleSelectAllFiltered()}
                  aria-label="בחר את כל הפריטים המוצגים"
                  disabled={isSavingLimit}
                />
              </span>
            )}
            <span className={classes.listHeaderProduct}>מוצר</span>
          </div>
          {hasSelectionActions && (
            <div className={classes.selectionActions}>
              {isManager && (
                <Button
                  type="button"
                  data-testid="limit-items-bulk-limit"
                  className={`${classes.outlineButton} ${classes.outlineButtonInfo}`}
                  onClick={handleOpenBulkNumPad}
                  disabled={isSavingLimit}
                >
                  בחר הגבלה
                </Button>
              )}
              {isManager && (
                <Button
                  type="button"
                  data-testid="limit-items-clear-selection"
                  className={`${classes.outlineButton} ${classes.outlineButtonNeutral}`}
                  onClick={handleClearSelection}
                  disabled={isSavingLimit}
                >
                  בטל בחירה
                </Button>
              )}
            </div>
          )}
          <div className={classes.qtyHeaderCell}>
            <span className={classes.qtyHeaderLabel}>כמות במלאי</span>
            {isManager && hasClearAction && (
              <Button
                type="button"
                data-testid="limit-items-clear-limits"
                className={`${classes.outlineButton} ${classes.outlineButtonDanger} ${classes.headerResetButton}`}
                onClick={handleClearLimits}
                disabled={isSavingLimit}
              >
                {clearButtonLabel}
              </Button>
            )}
          </div>
        </div>

        <div className={classes.listScroll} ref={listScrollRef} data-testid="limit-items-list">
          {isLoadingPosProducts && posProducts.length === 0 ? (
            <p className={classes.empty}>טוען מוצרים...</p>
          ) : filteredProducts.length === 0 ? (
            <p className={classes.empty}>אין מוצרים להצגה</p>
          ) : null}
          {showList && (
            <div className={classes.virtualCanvas} style={{ height: rowVirtualizer.getTotalSize() }}>
              {virtualRows.map((virtualRow) => {
                const product = filteredProducts[virtualRow.index];
                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    className={classes.virtualRow}
                    style={{ transform: `translateY(${virtualRow.start}px)` }}
                  >
                    <LimitItemRow
                      id={product.id}
                      name={product.name}
                      limit={product.limit ?? null}
                      isSelected={selectedProductIds.has(product.id)}
                      isManager={isManager}
                      interactionsLocked={interactionsLocked}
                      onOpenNumPad={handleOpenNumPad}
                      onToggleSelected={handleToggleSelected}
                      onClearLimit={handleClearLimit}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <NumPadModal
        dataTestId="limit-items-numpad"
        isOpen={numPadOpen}
        title={numPadTitle}
        subjectName={numPadSubject}
        initialValue={numPadInitial}
        maxDigits={2}
        isSubmitting={isSavingLimit}
        error={limitError}
        onClose={handleCloseNumPad}
        onConfirm={handleConfirmNumPad}
      />

      <Modal
        opened={resetConfirm !== null}
        onClose={() => setResetConfirm(null)}
        title="איפוס כל ההגבלות"
        centered
        dir="rtl"
        radius="xl"
        closeButtonProps={{ 'data-testid': 'limit-items-reset-close' } as Record<string, string>}
        classNames={{
          content: classes.confirmModalContent,
          header: classes.confirmModalHeader,
          title: classes.confirmModalTitle,
          body: classes.confirmModalBody,
        }}
      >
        <p className={classes.confirmText}>
          {resetConfirmMessage}
        </p>
        <div className={classes.confirmActions}>
          <Button
            type="button"
            data-testid="limit-items-reset-cancel"
            className={classes.confirmCancelButton}
            onClick={() => setResetConfirm(null)}
            disabled={isSavingLimit}
          >
            ביטול
          </Button>
          <Button
            type="button"
            data-testid="limit-items-reset-confirm"
            className={classes.confirmDangerButton}
            onClick={handleConfirmReset}
            disabled={isSavingLimit}
          >
            אפס הכל
          </Button>
        </div>
      </Modal>
    </section>
  );
};

export const PosLimitItemsSection = React.memo(PosLimitItemsSectionComponent);
